import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class Country extends BaseObj {
    public ID: number;
    public __id: number;

    public Continent: string;
    public __continent: string;

    public DialCode: string | null;
    public __dialcode: string | null;

    public DialMask: string | null;
    public __dialmask: string | null;

    public FIFA: string | null;
    public __fifa: string | null;

    public IOC: string | null;
    public __ioc: string | null;

    public ISO2: string;
    public __iso2: string;

    public ISO3: string | null;
    public __iso3: string | null;

    public Name: string;
    public __name: string;

    public readonly TranslationKey: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Continent?: string | null;
        DialCode?: string | null;
        DialMask?: string | null;
        EntityState?: EntityState;
        FIFA?: string | null;
        IOC?: string | null;
        ISO2?: string | null;
        ISO3?: string | null;
        Name?: string | null;
        TranslationKey?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Country>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Country tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Continent = values.Continent ?? '';
        this.__continent = values.Continent ?? '';
        Object.defineProperty(this, 'Continent', {
            get(): string {
                return this.__continent;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Continent', this.__continent, value);
                this.__continent = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DialCode = values.DialCode ?? null;
        this.__dialcode = values.DialCode ?? null;
        Object.defineProperty(this, 'DialCode', {
            get(): string | null {
                return this.__dialcode;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DialCode', this.__dialcode, value);
                this.__dialcode = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DialMask = values.DialMask ?? null;
        this.__dialmask = values.DialMask ?? null;
        Object.defineProperty(this, 'DialMask', {
            get(): string | null {
                return this.__dialmask;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DialMask', this.__dialmask, value);
                this.__dialmask = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FIFA = values.FIFA ?? null;
        this.__fifa = values.FIFA ?? null;
        Object.defineProperty(this, 'FIFA', {
            get(): string | null {
                return this.__fifa;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('FIFA', this.__fifa, value);
                this.__fifa = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IOC = values.IOC ?? null;
        this.__ioc = values.IOC ?? null;
        Object.defineProperty(this, 'IOC', {
            get(): string | null {
                return this.__ioc;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('IOC', this.__ioc, value);
                this.__ioc = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISO2 = values.ISO2 ?? '';
        this.__iso2 = values.ISO2 ?? '';
        Object.defineProperty(this, 'ISO2', {
            get(): string {
                return this.__iso2;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('ISO2', this.__iso2, value);
                this.__iso2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISO3 = values.ISO3 ?? null;
        this.__iso3 = values.ISO3 ?? null;
        Object.defineProperty(this, 'ISO3', {
            get(): string | null {
                return this.__iso3;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ISO3', this.__iso3, value);
                this.__iso3 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TranslationKey = values.TranslationKey ?? '';
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Continent": ${JSON.stringify(this.Continent)}` +
            ',' +
            `"DialCode": ${JSON.stringify(this.DialCode)}` +
            ',' +
            `"DialMask": ${JSON.stringify(this.DialMask)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"FIFA": ${JSON.stringify(this.FIFA)}` +
            ',' +
            `"IOC": ${JSON.stringify(this.IOC)}` +
            ',' +
            `"ISO2": ${JSON.stringify(this.ISO2)}` +
            ',' +
            `"ISO3": ${JSON.stringify(this.ISO3)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"TranslationKey": ${JSON.stringify(this.TranslationKey)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Country from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Continent?: string | null;
        DialCode?: string | null;
        DialMask?: string | null;
        FIFA?: string | null;
        IOC?: string | null;
        ISO2?: string | null;
        ISO3?: string | null;
        Name?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Country {
        return new Country(values);
    }

    public static _FromServerJson(json: any): Country {
        return new Country({
            ID: json.ID,
            Continent: json.Continent ?? '',
            DialCode: json.DialCode ?? '',
            DialMask: json.DialMask ?? '',
            FIFA: json.FIFA ?? '',
            IOC: json.IOC ?? '',
            ISO2: json.ISO2 ?? '',
            ISO3: json.ISO3 ?? '',
            Name: json.Name ?? '',
            TranslationKey: json.TranslationKey ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Country from another instance of Country.
     * @param other The Country you want to clone
     * @returns A cloned Country
     */
    public static FromOther(other: Country): Country {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Country._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Country.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ID' | 'Continent' | 'DialCode' | 'DialMask' | 'FIFA' | 'IOC' | 'ISO2' | 'ISO3' | 'Name' | 'TranslationKey')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Country';
}
